import type { MapStyle } from '../../api/types'

/**
 * Die Farben auf der Karte — je Zustand eine, an **einer** Stelle.
 *
 * Drei Dinge hängen an dieser Liste: die Regler auf der Karte, die
 * CSS-Variablen am Kartenrahmen und die eingebauten Vorgaben. Sie an drei
 * Stellen zu führen wäre der sichere Weg zu einer Karte, auf der ein Regler
 * eine Farbe ändert, die es im Stylesheet gar nicht gibt.
 *
 * **Die Vorgaben stehen doppelt**, hier und in `Settings::DARSTELLUNG_TEXT` —
 * aus demselben Grund wie bei der Linienfarbe: die Karte soll zeichnen können,
 * bevor die Einstellungen da sind. Eine Nadel, die eine Sekunde lang unsichtbar
 * ist, wäre schlimmer als eine, die kurz die Vorgabefarbe trägt.
 *
 * **Warum überhaupt einstellbar:** welche Farben vier Zustände auseinanderhalten,
 * hängt vom Kartenbild ab. Auf OpenTopoMap mit seinen braunen Höhenlinien
 * verschwindet ein Ocker, das auf dem Standardbild klar heraussticht.
 *
 * **Der Name des Feldes steht nicht hier, sondern in `texte/de.ts`**
 * (`karte.nadelFelder`), nach demselben Schnitt wie bei den Sprechblasen: die
 * Mechanik bleibt, wo sie wirkt, die Wörter liegen an einer Stelle.
 */
export const NADELFARBEN = [
  {
    key: 'pin_color',
    variable: '--nadel',
    vorgabe: '#007aff',
  },
  {
    key: 'pin_draft_color',
    variable: '--nadel-entwurf',
    vorgabe: '#8e8e93',
  },
  {
    key: 'pin_suggestion_color',
    variable: '--nadel-vorschlag',
    vorgabe: '#ff9500',
  },
  {
    key: 'pin_selected_color',
    variable: '--nadel-gewaehlt',
    vorgabe: '#5856d6',
  },
  {
    key: 'pin_editing_color',
    variable: '--nadel-offen',
    vorgabe: '#ff3b30',
  },
  /**
   * Keine Nadel, aber derselbe Regler — und mit Absicht in derselben Liste.
   *
   * Es ist die Fläche, die ein Highlight **ohne Bild** trägt. Sie gehört zur
   * Karte, wo man sie einstellt, gilt aber auch für die Kärtchen im Chat; die
   * liegen außerhalb der Karte, deshalb setzt die `Shell` diese Variable
   * zusätzlich am Wurzelelement.
   */
  {
    key: 'card_color',
    variable: '--kaertchen',
    vorgabe: '#007aff',
  },
] as const

/** Der Name einer dieser Farben, wie ihn der Server kennt. */
export type Nadelfarbe = (typeof NADELFARBEN)[number]['key']

/**
 * Was gerade gilt: erst der Regler, dann die Reise, dann die Vorgabe.
 *
 * Dieselbe Reihenfolge wie beim Strich — `eigen` hat Vorrang, damit die Nadeln
 * **beim Wählen** mitgehen und nicht erst nach der Antwort des Servers.
 *
 * **Die Reise und nicht die Einstellungen** (#50): die Farben stehen seitdem
 * an der Reise, beim Anlegen aus der Voreinstellung kopiert. Die Vorgabe
 * trägt nur den Augenblick, bevor die Reise geladen ist.
 */
export function nadelfarben(
  eigen: Partial<Record<Nadelfarbe, string>>,
  werte: MapStyle | undefined,
): Record<Nadelfarbe, string> {
  const raus = {} as Record<Nadelfarbe, string>
  for (const eintrag of NADELFARBEN) {
    raus[eintrag.key] = eigen[eintrag.key] ?? werte?.[eintrag.key] ?? eintrag.vorgabe
  }
  return raus
}

/** Dieselben Farben als CSS-Variablen für den Kartenrahmen. */
export function alsVariablen(farben: Record<Nadelfarbe, string>): Record<string, string> {
  const raus: Record<string, string> = {}
  for (const eintrag of NADELFARBEN) raus[eintrag.variable] = farben[eintrag.key]
  return raus
}
